import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { mintaTeks } from "../lib/dialogAplikasi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

/* K182 (vonis Owner 20 Sep 2026; AUDIT-B2 23 Sep 2026) — MASTER KATEGORI KOMPLAIN, Owner saja.
   Kategori komplain dikunci sebagai master: Owner menambah, mengganti nama, mengurutkan, dan
   menonaktifkan — tidak ada hapus (tiap komplain menunjuk kodenya lewat FK). "Lainnya" wajib
   aktif: komplain yang tidak cocok kategori mana pun jatuh ke sana, dan yang berulang di sana
   adalah sinyal kategori baru. Sumber: GET/POST/PATCH /api/master/kategori-komplain. */

export default function MasterKategoriKomplain() {
  const [baris, setBaris] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pola daftar seragam (28 Sep 2026) — master KECIL: cari/halaman di klien; urutan tampil = `sort_order`
  // (urutan yang dipilih staf), jadi kolom tidak bisa diurut ulang. Batas baris diawasi
  // `backend/tests/test_batas_master_kecil_lokal.py`.
  const daftar = useDaftar(baris, {
    kunciCari: ["label_id", "code"],
    kolom: [{ kunci: "sort_order", judul: "Urutan" }, { kunci: "label_id", judul: "Kategori" }, { kunci: "code", judul: "Kode" },
      { kunci: "is_active", judul: "Status", ambil: (k) => (k.is_active ? "Aktif" : "Nonaktif") }],
  });
  const sedangMencari = daftar.cari.trim() !== "";
  const [labelBaru, setLabelBaru] = useState("");
  const [sibuk, setSibuk] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/kategori-komplain", { params: { semua: 1 } });
      setBaris(Array.isArray(r.data) ? r.data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat kategori komplain.")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function tambah() {
    setSibuk("baru");
    try {
      const r = await api.post("/master/kategori-komplain", { label: labelBaru.trim() });
      toast.success(`Kategori "${r.data.label_id}" ditambahkan.`);
      setLabelBaru("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Kategori tidak bisa ditambahkan.")); }
    finally { setSibuk(null); }
  }

  async function ubah(k, badan, pesan) {
    setSibuk(k.code);
    try {
      await api.patch(`/master/kategori-komplain/${k.code}`, badan);
      toast.success(pesan);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Perubahan ditolak.")); }
    finally { setSibuk(null); }
  }

  async function gantiNama(k) {
    const label = await mintaTeks(`Nama baru untuk kategori "${k.label_id}":`, { awal: k.label_id, multiBaris: false });
    if (!label || label.trim() === k.label_id) return;
    ubah(k, { label: label.trim() }, "Nama kategori diganti.");
  }

  function geser(i, arah) {
    // `i` = indeks di SELURUH daftar (bukan di halaman) — tetangga yang ditukar selalu tetangga sebenarnya.
    const lain = baris[i + arah];
    const k = baris[i];
    if (!lain || k.code === "LAINNYA" || lain.code === "LAINNYA") return;
    // tukar urutan dua baris bertetangga
    Promise.all([
      api.patch(`/master/kategori-komplain/${k.code}`, { sort_order: lain.sort_order }),
      api.patch(`/master/kategori-komplain/${lain.code}`, { sort_order: k.sort_order }),
    ]).then(load).catch(e => toast.error(apiErrorMessage(e, "Urutan gagal diubah.")));
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="master-kategori-komplain">
      <div>
        <h1 className="text-xl font-semibold">Master Kategori Komplain</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Daftar yang dipilih staf saat mencatat komplain customer. Tidak ada hapus — nonaktifkan. Kategori
          nonaktif tidak ditawarkan lagi, komplain lama tetap memakai kategorinya. "Lainnya" selalu aktif.
        </p>
        <p className="text-sm mt-1">Master alasan lain:{" "}
          <Link to="/master/alasan/revisi-desain" className="underline" data-testid="kk-ke-alasan-revisi">revisi desain</Link>{" · "}
          <Link to="/master/alasan/tutup-temuan" className="underline" data-testid="kk-ke-alasan-temuan">tutup temuan Papan Kontrol</Link>
        </p>
      </div>

      <Card className="p-3 flex flex-wrap gap-2 items-center">
        <Input className="max-w-xs" placeholder="Nama kategori baru" value={labelBaru}
               onChange={e => setLabelBaru(e.target.value)} data-testid="kk-label-baru" />
        <Button onClick={tambah} disabled={labelBaru.trim().length < 2 || sibuk === "baru"} data-testid="kk-tambah">
          <Plus size={16} className="mr-1" />Tambah
        </Button>
      </Card>

      <PalangDaftar daftar={daftar} placeholder="Cari kategori / kode…" testId="kk-palang"
                    ekspor={{ nama: "kategori_komplain", kolom: daftar.kolom }} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Urutan</th>
              <th className="text-left px-3 py-2">Kategori</th>
              <th className="text-left px-3 py-2">Kode</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-4 text-muted-foreground">Memuat…</td></tr>}
            {!loading && daftar.total === 0 && <tr><td colSpan={5} className="px-3 py-4 text-muted-foreground">
              {sedangMencari ? "Tidak ada yang cocok." : "Belum ada kategori."}</td></tr>}
            {!loading && daftar.tampil.map((k) => { const i = baris.indexOf(k); return (
              <tr key={k.code} className="border-t" data-testid={`kk-baris-${k.code}`}>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Button size="sm" variant="ghost" disabled={sedangMencari || k.code === "LAINNYA" || i === 0}
                          onClick={() => geser(i, -1)} aria-label="Naikkan">↑</Button>
                  <Button size="sm" variant="ghost" disabled={sedangMencari || k.code === "LAINNYA" || baris[i + 1]?.code === "LAINNYA" || i === baris.length - 1}
                          onClick={() => geser(i, 1)} aria-label="Turunkan">↓</Button>
                </td>
                <td className="px-3 py-2">{k.label_id}</td>
                <td className="px-3 py-2 font-mono text-xs">{k.code}</td>
                <td className="px-3 py-2">
                  {k.is_active ? <Badge variant="secondary">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                  <Button size="sm" variant="outline" disabled={sibuk === k.code} onClick={() => gantiNama(k)}>Ganti nama</Button>
                  {k.code !== "LAINNYA" && (
                    <Button size="sm" variant="outline" disabled={sibuk === k.code}
                            onClick={() => ubah(k, { is_active: !k.is_active },
                                                k.is_active ? "Kategori dinonaktifkan." : "Kategori diaktifkan kembali.")}
                            data-testid={`kk-saklar-${k.code}`}>
                      {k.is_active ? "Nonaktifkan" : "Aktifkan"}
                    </Button>
                  )}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="kk-paginasi" />
      </Card>
    </div>
  );
}
